import { Save } from 'lucide-react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../helpers/axiosConfig';
import { rehydrate } from '../../reducers/authSlice';

const splitFullName = (fullName) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  const firstName = parts.shift() || '';
  const lastName = parts.join(' ') || firstName;
  return { firstName, lastName };
};

export default function SettingsPage() {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.auth.user);
  const [fullName, setFullName] = useState(`${user?.first_name || ''} ${user?.last_name || ''}`.trim());
  const [email, setEmail] = useState(user?.email || '');
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');

    try {
      const token = localStorage.getItem('CloudForge-Token');
      const { firstName, lastName } = splitFullName(fullName);
      const payload = {
        first_name: firstName,
        last_name: lastName,
        email,
      };
      if (password) payload.password = password;

      await axiosInstance.put(`users/${user.id}`, payload, {
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
      });
      dispatch(rehydrate());
      setPassword('');
      setMessage('Settings saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.msg || requestError.response?.data?.error || requestError.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Settings</h1>
          <div className="page-subtitle">Workspace profile and account security.</div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" type="submit" form="settings-form" disabled={saving}>
            <Save size={15} />
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="settings-layout">
        <aside className="settings-nav">
          <div>
            <span>Workspace</span>
            <a className="active" href="#profile">Profile & account</a>
            <a href="#api">API keys</a>
          </div>
          <div>
            <span>Defaults</span>
            <a href="#email">Email & notifications</a>
            <a href="#domains">Domains & DNS</a>
          </div>
          <div>
            <span>Danger zone</span>
            <a href="#export">Export data</a>
            <a className="danger" href="#delete">Delete workspace</a>
          </div>
        </aside>

        <form className="settings-content" id="settings-form" onSubmit={saveProfile}>
          {message ? <div className="success-banner">{message}</div> : null}
          {error ? <div className="error-banner">{error}</div> : null}

          <section className="settings-section panel-card" id="profile">
            <div className="settings-section-head">
              <span>Profile</span>
              <h2>Profile & account</h2>
            </div>
            <div className="settings-row">
              <div>
                <strong>Full name</strong>
                <p>Shown in your workspace and support requests.</p>
              </div>
              <input value={fullName} onChange={(event) => setFullName(event.target.value)} required />
            </div>
            <div className="settings-row">
              <div>
                <strong>Email</strong>
                <p>Used for login and WordPress admin defaults.</p>
              </div>
              <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required />
            </div>
            <div className="settings-row">
              <div>
                <strong>Workspace name</strong>
                <p>Workspace records are not editable yet.</p>
              </div>
              <input value="North workspace" disabled />
            </div>
          </section>

          <section className="settings-section panel-card" id="security">
            <div className="settings-section-head">
              <span>Security</span>
              <h2>Security</h2>
            </div>
            <div className="settings-row">
              <div>
                <strong>Two-factor authentication</strong>
                <p>Authenticator-app support will be added after the auth provider is finalized.</p>
              </div>
              <span className="settings-value">Not enabled</span>
            </div>
            <div className="settings-row">
              <div>
                <strong>North account password</strong>
                <p>Leave blank to keep your current password.</p>
              </div>
              <input type="password" minLength={10} value={password} onChange={(event) => setPassword(event.target.value)} />
            </div>
          </section>
        </form>
      </div>
    </>
  );
}
